<!-- src/views/auth/ChangePassword.vue -->
<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div>
        <img class="mx-auto h-16 w-auto" src="@/assets/images/logo.png" alt="iOr">
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {{ $t('motDePasse.titre') }}
        </h2>
        <p class="mt-2 text-center text-sm text-gray-600">
          {{ isFirstLogin ? $t('motDePasse.premiere') : $t('motDePasse.modification') }}
        </p>
      </div>
      
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      
      <div v-if="success" class="alert alert-success">
        {{ success }}
      </div>
      
      <form class="mt-8 space-y-6" @submit.prevent="handleChangePassword">
        <div class="rounded-md shadow-sm -space-y-px">
          <div>
            <label for="currentPassword" class="sr-only">{{ $t('motDePasse.actuel') }}</label>
            <input id="currentPassword" name="currentPassword" type="password" v-model="form.currentPassword" required
                   class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                   :placeholder="$t('motDePasse.actuel')">
          </div>
          <div>
            <label for="newPassword" class="sr-only">{{ $t('motDePasse.nouveau') }}</label>
            <input id="newPassword" name="newPassword" type="password" v-model="form.newPassword" required
                   class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                   :placeholder="$t('motDePasse.nouveau')">
          </div>
          <div>
            <label for="confirmPassword" class="sr-only">{{ $t('motDePasse.confirmer') }}</label>
            <input id="confirmPassword" name="confirmPassword" type="password" v-model="form.confirmPassword" required
                   class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                   :placeholder="$t('motDePasse.confirmer')">
          </div>
        </div>

        <div>
          <button type="submit" class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  :disabled="loading || passwordMismatch">
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            {{ $t('motDePasse.changer') }}
          </button>
        </div>
        
        <div v-if="passwordMismatch" class="text-sm text-danger text-center">
          {{ $t('motDePasse.differents') }}
        </div>
        
        <div class="flex items-center justify-center">
          <button type="button" @click="goToHome" class="text-sm text-primary-600 hover:text-primary-500">
            {{ $t('motDePasse.accueil') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, computed, ref, onMounted } from 'vue';
import nextRoute from '@/services/next-route';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { texteErreur } from '@/i18n/serveur';

export default defineComponent({
  name: 'ChangePassword',
  
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const router = useRouter();
    
    const isFirstLogin = ref(false);
    
    onMounted(() => {
      // Vérifier si c'est la première connexion
      const user = store.getters['auth/user'];
      if (user && user.firstLogin) {
        isFirstLogin.value = true;
      }
      
      // Vérifier si l'utilisateur est authentifié
      if (!store.getters['auth/isAuthenticated']) {
        router.push('/login');
      }
    });
    
    const form = reactive({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    
    const success = ref('');
    
    const loading = computed(() => store.getters['auth/loading']);
    // L'erreur du store est le texte français du serveur : on garde l'erreur reçue pour la
    // traduire (en français, texteErreur rend ce même texte mot pour mot).
    const derniereErreur = ref(null);
    const error = computed(() => {
      const e = store.getters['auth/error'];
      if (!e || !derniereErreur.value) return e;
      return texteErreur(derniereErreur.value, t('motDePasse.echec'));
    });
    const passwordMismatch = computed(() => 
      form.newPassword && form.confirmPassword && form.newPassword !== form.confirmPassword
    );
    
    const userRole = computed(() => {
      const user = store.getters['auth/user'];
      return user ? user.role : '';
    });
    
    const handleChangePassword = async () => {
      if (passwordMismatch.value) {
        return;
      }
      
      derniereErreur.value = null;
      try {
        const changePasswordAction = userRole.value === 'admin' 
          ? 'auth/changePassword' 
          : 'auth/changeClientPassword';
        
        await store.dispatch(changePasswordAction, {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword
        });
        
        success.value = t('motDePasse.succes');
        
        // Une destination en attente traverse cet écran : un client tout neuf qui a scanné le
        // QR de rattachement passe forcément par ici (première connexion), et son code ne
        // survit que s'il retrouve la page de rattachement ensuite.
        const suite = nextRoute.consommer();

        // Rediriger après un court délai
        setTimeout(() => {
          if (suite) {
            window.location.href = suite;
          } else if (userRole.value === 'admin') {
            window.location.href = '/admin';
          } else {
            window.location.href = '/client';
          }
        }, 2000);
      } catch (err) {
        derniereErreur.value = err;
        console.error('Erreur de changement de mot de passe:', err);
      }
    };
    
    const goToHome = () => {
      if (userRole.value === 'admin') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/client';
      }
    };
    
    return {
      form,
      loading,
      error,
      success,
      passwordMismatch,
      handleChangePassword,
      goToHome,
      isFirstLogin,
      userRole
    };
  }
});
</script>
